import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import DetailActions from "@/components/feature/DetailActions";
import EmptyState from "@/components/base/EmptyState";
import { newsList, getNewsById } from "@/mocks/news";
import { getMovieById } from "@/mocks/movies";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const news = id ? getNewsById(id) : undefined;

  const related = useMemo(() => {
    if (!news) return [];
    return newsList
      .filter((n) => n.id !== news.id)
      .sort((a, b) => (a.category === news.category ? -1 : 0) - (b.category === news.category ? -1 : 0))
      .slice(0, 4);
  }, [news]);

  useDocumentMeta(
    news ? `${news.title} | 小黄人电影` : "资讯详情 | 小黄人电影",
    news ? news.summary.slice(0, 100) : undefined
  );

  if (!news) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-[1280px] px-4 py-20 md:px-6">
          <EmptyState
            icon="ri-newspaper-line"
            title="资讯不存在"
            desc="这篇资讯可能已被删除或链接有误，去资讯列表看看别的内容吧。"
            actionLabel="返回资讯列表"
            actionTo="/news"
            actionIcon="ri-arrow-left-line"
          />
        </div>
      </SiteLayout>
    );
  }

  const movie = getMovieById(news.relatedMovieId);

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[860px] px-4 py-8 md:px-6 md:py-10">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "影视资讯", to: "/news" },
            { label: news.category },
          ]}
        />

        <div className="mt-6">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-800">
            <i className="ri-price-tag-3-line"></i>
            {news.category}
          </span>
          <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
            {news.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {news.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line"></i>
              {news.views.toLocaleString()} 次阅读
            </span>
          </div>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-background-200">
          <img
            src={news.cover}
            alt={news.title}
            title={`${news.title} 小黄人电影 资讯`}
            className="aspect-[16/9] w-full object-cover object-top"
          />
        </div>

        <article className="mt-8 space-y-5">
          {news.body.map((para, index) => (
            <p key={index} className="text-[15px] leading-8 text-foreground-700">
              {para}
            </p>
          ))}
        </article>

        <div className="mt-8 rounded-2xl border border-background-200 bg-background-50 p-5">
          <DetailActions
            type="news"
            id={news.id}
            title={news.title}
            path={`/news/${news.id}`}
          />
        </div>

        {movie && (
          <div className="mt-6 flex flex-wrap items-center gap-4 rounded-2xl bg-secondary-50 p-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary-100 text-secondary-700">
              <i className="ri-film-line text-xl"></i>
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-foreground-500">相关影片</p>
              <p className="text-sm font-semibold text-foreground-950">{movie.title}</p>
            </div>
            <Link
              to={`/detail/${movie.id}`}
              className="flex items-center gap-1.5 rounded-full bg-secondary-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-secondary-600"
            >
              查看详情
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        )}

        <section className="mt-12">
          <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
              <i className="ri-links-line"></i>
            </span>
            相关资讯
          </h2>
          <ul className="mt-4 space-y-2">
            {related.map((item) => (
              <li key={item.id}>
                <Link
                  to={`/news/${item.id}`}
                  className="group flex items-center gap-3 rounded-xl border border-background-200 bg-background-50 px-4 py-3.5 transition hover:border-primary-300"
                >
                  <i className="ri-article-line text-lg text-primary-600"></i>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground-900 group-hover:text-primary-700">
                      {item.title}
                    </span>
                    <span className="mt-0.5 block text-xs text-foreground-500">
                      {item.date} · {item.category}
                    </span>
                  </span>
                  <i className="ri-arrow-right-s-line text-foreground-400"></i>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </SiteLayout>
  );
}